import { gql } from "@apollo/client";

const FIND_PRODUCT = gql`
  query FindProduct($productId: ID) {
    findProduct(productId: $productId) {
      ... on BrandingProduct {
        _id
        createdAt
        homeUrl
        logo
        smallLogo
        name
        primaryColor
        productType
        secondaryColor
        subdomain
        backgroundColor
        tertiaryColor
        mobileNavStyle
        brandEmails
        emailLogo
        emailSupportOverride {
          teamName
          email
          phone
          address
        }
        feeSchedule {
          _id
          feeName
          isDefault
          forProductType
          billingFrequency
          tiers {
            minAmount
            maxAmount
            feePercentage
          }
          yearlyMinimumFee
          adminFee {
            enabled
            method
            flatAmount
          }
          arrearFee {
            enabled
            method
            tierOffsetSource
            proRate
          }
          flatFee {
            enabled
            amount
            status
            startDate
            nextExecutionDate
            cancellationDate
          }
        }
      }
    }
  }
`;

const FIND_ALL_PRODUCTS = gql`
  query FindAllProducts($page: Int, $pageSize: Int) {
    findAllProducts(page: $page, pageSize: $pageSize) {
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
      products {
        ... on DafProduct {
          _id
          name
          productType
          totals {
            availableToGrant
            date
            cash
            crypto
            stock
            alternativeAssets
            pendingGrantPayments
            totalContributed
            totalGranted
            ytdContributed
            ytdGranted
          }
          users {
            _id
            associationType
            user {
              ... on InternalUser {
                _id
                firstName
                lastName
              }
              ... on DonorUser {
                _id
                firstName
                lastName
              }
              ... on FirmUser {
                _id
                firstName
                lastName
              }
              ... on EntityUser {
                _id
                firstName
                lastName
              }
            }
          }
        }
        ... on CommunityFundProduct {
          _id
          name
          productType
          totals {
            availableToGrant
            date
            cash
            crypto
            stock
            alternativeAssets
            pendingGrantPayments
            totalContributed
            totalGranted
            ytdContributed
            ytdGranted
          }
          users {
            _id
            associationType
            user {
              ... on InternalUser {
                _id
                firstName
                lastName
              }
              ... on DonorUser {
                _id
                firstName
                lastName
              }
              ... on FirmUser {
                _id
                firstName
                lastName
              }
              ... on EntityUser {
                _id
                firstName
                lastName
              }
            }
          }
        }
        ... on ClatProduct {
          _id
          name
          productType
          totals {
            date
            cash
            stock
          }
          users {
            _id
            associationType
            user {
              ... on InternalUser {
                _id
                firstName
                lastName
              }
              ... on DonorUser {
                _id
                firstName
                lastName
              }
              ... on FirmUser {
                _id
                firstName
                lastName
              }
              ... on EntityUser {
                _id
                firstName
                lastName
              }
            }
          }
        }
        ... on BrandingProduct {
          _id
          name
          productType
          users {
            _id
            associationType
            user {
              ... on InternalUser {
                _id
                firstName
                lastName
              }
              ... on DonorUser {
                _id
                firstName
                lastName
              }
              ... on FirmUser {
                _id
                firstName
                lastName
              }
              ... on EntityUser {
                _id
                firstName
                lastName
              }
            }
          }
        }
      }
    }
  }
`;

const additionalUsersProductFields = `
  _id
  name
  users {
    _id
    associationType
    inviteStatus
    relationship
    expectedFirstName
    expectedLastName
    expectedEmail
    user {
      ... on DonorUser {
        _id
        firstName
        lastName
        email
      }
    }
  }
`;

const FIND_PRODUCT_ADDITIONAL_USERS = gql`
  query FindProductAdditionalUsers($productId: ID!) {
    findProductAdditionalUsers(productId: $productId) {
      ... on DafProduct {
        ${additionalUsersProductFields}
      }
      ... on CommunityFundProduct {
        ${additionalUsersProductFields}
      }
      ... on ClatProduct {
        ${additionalUsersProductFields}
      }
      ... on BrandingProduct {
        ${additionalUsersProductFields}
      }
    }
  }
`;

const FIND_BRANDING = gql`
  query FindBranding($subdomain: String!) {
    findBranding(subdomain: $subdomain) {
      _id
      homeUrl
      logo
      smallLogo
      primaryColor
      secondaryColor
      backgroundColor
      tertiaryColor
      mobileNavStyle
      brandEmails
      emailLogo
      emailSupportOverride {
        teamName
        email
        phone
        address
      }
    }
  }
`;

const FIND_ENTITY_PRODUCTS = gql`
  query FindEntityProducts($entityId: ID!) {
    findEntityProducts(entityId: $entityId) {
      _id
      product {
        ... on DafProduct {
          _id
          name
          productType
        }
        ... on ClatProduct {
          _id
          name
          productType
        }
        ... on BrandingProduct {
          _id
          name
          productType
        }
        ... on CommunityFundProduct {
          _id
          name
          productType
        }
      }
    }
  }
`;

const FIND_PRODUCTS_SERVER_SIDE = gql`
  query FindProductsServerSide($input: ServerQueryInput) {
    findProductsServerSide(input: $input) {
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
      products {
        ... on DafProduct {
          _id
          friendlyId
          name
          productType
          productStatus
          ownerId
          entityId
          totals {
            cash
            stock
            alternativeAssets
            crypto
            balance
            availableToGrant
          }
        }
        ... on CommunityFundProduct {
          _id
          friendlyId
          name
          productType
          productStatus
          ownerId
          entityId
          totals {
            cash
            stock
            alternativeAssets
            crypto
            balance
            availableToGrant
          }
        }
        ... on ClatProduct {
          _id
          friendlyId
          name
          productType
          productStatus
          ownerId
          entityId
          totals {
            cash
            stock
          }
        }
        ... on BrandingProduct {
          _id
          friendlyId
          name
          productType
          productStatus
          ownerId
          entityId
        }
      }
    }
  }
`;

const FIND_PRODUCTS_FOR_TASK_LINKING = gql`
  query FindProductsForTaskLinking($input: ServerQueryInput) {
    findProductsServerSide(input: $input) {
      products {
        ... on DafProduct {
          _id
          name
          productType
          productStatus
          ownerId
          entityId
        }
        ... on CommunityFundProduct {
          _id
          name
          productType
          productStatus
          ownerId
          entityId
        }
        ... on ClatProduct {
          _id
          name
          productType
          productStatus
          ownerId
          entityId
        }
        ... on BrandingProduct {
          _id
          name
          productType
          productStatus
          ownerId
          entityId
        }
      }
    }
  }
`;

// Pre-flight gate for the close-product UI. Mirrors the hard-block checks
// in serviceCloseProduct so the FE can disable the Close button and tell
// the user exactly why before they commit.
const GET_PRODUCT_CLOSE_ELIGIBILITY = gql`
  query GetProductCloseEligibility($productId: ID!) {
    getProductCloseEligibility(productId: $productId) {
      canClose
      outstandingFeeCount
      outstandingFeeAmount
      alreadyClosed
      projectedFee
      projectedFeeQuarter
      projectedFeeYear
    }
  }
`;

export {
  FIND_PRODUCT,
  FIND_ALL_PRODUCTS,
  FIND_PRODUCT_ADDITIONAL_USERS,
  FIND_BRANDING,
  FIND_ENTITY_PRODUCTS,
  FIND_PRODUCTS_SERVER_SIDE,
  FIND_PRODUCTS_FOR_TASK_LINKING,
  GET_PRODUCT_CLOSE_ELIGIBILITY,
};
